/**
 * MindUP curriculum materials registry
 * Maps material names/keywords to access URLs for educators
 * Extend this registry as new materials are identified in the curriculum.
 *
 * URLs: Use external MindUP resource URLs, or internal /materials/[slug] paths.
 * Add routes in /app/materials/[slug]/page.tsx to serve assets, or replace with
 * real URLs (e.g. MindUP portal, S3, etc.)
 */

export interface MaterialResource {
  slug: string;
  name: string;
  url: string;
  description?: string;
}

/** Keywords that map to a material resource (case-insensitive partial match) */
const MATERIAL_REGISTRY: MaterialResource[] = [
  {
    slug: "emotion-cards",
    name: "Emotion cards",
    url: "/materials/emotion-cards",
    description: "Printable cards for teaching emotional literacy",
  },
  {
    slug: "mindup-brain-poster",
    name: "MindUP Brain poster",
    url: "/materials/brain-poster",
    description: "Brain anatomy poster showing amygdala, hippocampus, PFC",
  },
  {
    slug: "my-mindful-brain-handout",
    name: "My Mindful Brain handout",
    url: "/materials/my-mindful-brain",
    description: "Student handout for brain structure lesson",
  },
  {
    slug: "mindful-me-portfolio",
    name: "Mindful Me Portfolio",
    url: "/materials/mindful-me-portfolio",
    description: "Portfolio template for student reflections",
  },
  {
    slug: "feelings-cards",
    name: "Feelings cards",
    url: "/materials/feelings-cards",
    description: "Cards for naming and exploring feelings",
  },
];

/**
 * Find a material resource URL for a given material string.
 * Uses partial, case-insensitive matching against registry names and slugs.
 */
export function getMaterialUrl(materialText: string): string | null {
  const normalized = materialText.toLowerCase().trim();
  for (const resource of MATERIAL_REGISTRY) {
    const nameNorm = resource.name.toLowerCase();
    const slugNorm = resource.slug.replace(/-/g, " ");
    if (
      normalized.includes(nameNorm) ||
      nameNorm.includes(normalized) ||
      normalized.includes(slugNorm)
    ) {
      return resource.url;
    }
  }
  return null;
}

/**
 * Get full resource details for a material string, if matched
 */
export function getMaterialResource(materialText: string): MaterialResource | null {
  const normalized = materialText.toLowerCase().trim();
  for (const resource of MATERIAL_REGISTRY) {
    const nameNorm = resource.name.toLowerCase();
    const slugNorm = resource.slug.replace(/-/g, " ");
    if (
      normalized.includes(nameNorm) ||
      nameNorm.includes(normalized) ||
      normalized.includes(slugNorm)
    ) {
      return resource;
    }
  }
  return null;
}

export { MATERIAL_REGISTRY };
